import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Divider,
  TextareaAutosize
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  NoteAdd as NoteAddIcon,
  Receipt as ReceiptIcon,
  Event as EventIcon
} from '@mui/icons-material';

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { customers, deleteCustomer, updateCustomer } = useData();
  const [newNote, setNewNote] = useState('');
  const customer = customers.find(c => c.id === id);

  if (!customer) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5">Customer not found</Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/customers')}
          sx={{ mt: 2 }}
        >
          Back to Customers
        </Button>
      </Box>
    );
  }

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      deleteCustomer(id);
      navigate('/customers');
    }
  };

  const handleAddNote = () => {
    if (!newNote.trim()) return;
    
    const notes = customer.notes || [];
    updateCustomer(id, {
      ...customer,
      notes: [...notes, {
        text: newNote,
        createdBy: 'Admin',
        createdAt: new Date().toISOString()
      }]
    });
    setNewNote('');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ p: 3, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }, 
        mb: 3,
        gap: 2
      }}>
        <Typography variant="h4">{customer.name}</Typography>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          width: { xs: '100%', sm: 'auto' }
        }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ReceiptIcon />}
            onClick={() => navigate(`/estimates/new?customerId=${id}`)}
            fullWidth
          >
            Create Estimate
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<EventIcon />}
            onClick={() => navigate(`/schedule?customerId=${id}`)}
            fullWidth
          >
            Schedule Estimate
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/customers/${id}/edit`, { state: { customerData: customer } })}
            fullWidth
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            fullWidth
          >
            Delete
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3, width: '100%' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">Contact Information</Typography>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">Email</Typography>
              <Typography variant="body1">{customer.email}</Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">Phone</Typography>
              <Typography variant="body1">{customer.phone}</Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">Address</Typography>
              <Typography variant="body1">{customer.address}</Typography>
              <Typography variant="body1">
                {customer.city}, {customer.zipCode}
              </Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">How did you find us?</Typography>
              <Typography variant="body1">{customer.referralSource || 'Not specified'}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">System Information</Typography>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">Created By</Typography>
              <Typography variant="body1">{customer.createdBy}</Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">Created At</Typography>
              <Typography variant="body1">{formatDate(customer.createdAt)}</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Notes Section */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Notes</Typography>
          <Typography variant="body2" color="text.secondary">
            {customer.notes?.length || 0} notes
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />

        {/* Add Note Form */}
        <Box sx={{ mb: 4 }}>
          <TextareaAutosize
            minRows={3}
            placeholder="Add a new note..."
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              marginBottom: '12px',
              fontFamily: 'inherit'
            }}
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleAddNote}
              disabled={!newNote.trim()}
              startIcon={<NoteAddIcon />}
            >
              Add Note
            </Button>
          </Box>
        </Box>

        {/* Notes List */}
        <Box>
          {customer.notes?.map((note, index) => (
            <Paper key={index} sx={{ p: 2, mb: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {note.text}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                By {note.createdBy} on {formatDate(note.createdAt)}
              </Typography>
            </Paper>
          ))}
          {(!customer.notes || customer.notes.length === 0) && (
            <Typography color="text.secondary" align="center">
              No notes yet
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
} 